<script lang="ts">
	import { afterNavigate } from '$app/navigation';
	import BigButton from '$lib/components/BigButton.svelte';
	import CartButton from '$lib/components/header/buttons/CartButton.svelte';
	import Lazy from '$lib/components/Lazy.svelte';
	import Price from '$lib/components/Price.svelte';
	import ProductCarousel from '$lib/components/product/ProductCarousel.svelte';
	import Skeleton from '$lib/components/Skeleton.svelte';
	import ThumbnailImage from '$lib/components/ThumbnailImage.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { getContext } from 'svelte';

	import type { CartProvider } from './store.svelte';
	const cart = getContext<CartProvider>('cart');

	let show = $state(false);

	type Product = { name: string; sku: string; quantity: number; image: string };
	let product: Product | null = $state(null);

	export function open({ name, sku, quantity, image }: Product) {
		product = { name, sku, quantity, image };
		show = true;
	}

	let oldDocumentOverflow = '';
	$effect(() => {
		if (show) {
			oldDocumentOverflow = document.documentElement.style.overflow;
			document.documentElement.style.overflow = 'hidden';
		}

		return () => {
			if (show) {
				document.documentElement.style.overflow = oldDocumentOverflow;
			}
		};
	});

	afterNavigate(() => {
		show = false;
	});
</script>

{#if show}
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div class="fixed inset-0 z-40 bg-black/60" onclick={() => (show = false)}></div>
{/if}

<div
	class={[
		'fixed right-0 bottom-0 left-0 z-50 max-h-dvh min-h-[70%] w-full max-w-screen overflow-y-auto rounded-t-2xl',
		'bg-white p-6 shadow-xl transition-transform duration-300 ease-in-out',
		'md:top-0 md:right-0 md:bottom-0 md:left-auto md:h-full md:w-[500px] md:rounded-none',
		show
			? 'translate-y-0 md:translate-x-0 md:translate-y-0'
			: 'translate-y-full md:translate-x-full md:translate-y-0',
	]}
>
	<div class="mb-8 flex items-center justify-between">
		<div class="flex items-center">
			<span class="icon-[mdi--check-circle] size-7 text-green-500"></span>
			<h2 class="ml-2 text-2xl font-bold">{m.added_to_cart()} !</h2>
		</div>
		<button
			class="icon-[mdi--window-close] size-9 cursor-pointer transition duration-200 hover:bg-neutral-400"
			onclick={() => (show = false)}
			aria-label={m.close()}
		></button>
	</div>
	<div class="flex w-full flex-col gap-4">
		<div class="mx-auto flex w-full flex-col">
			{#if product?.image}
				<div class="mx-auto mb-4 hidden w-2/5 bg-neutral-100 md:block">
					<ThumbnailImage
						src="product/{product.image}"
						defaultSize={300}
						maxSize={600}
						class="w-full mix-blend-darken"
					/>
				</div>
			{/if}
			<p class="line-clamp-2 text-center text-lg font-bold">
				{product?.quantity && product?.quantity > 1
					? `${product?.quantity} x ${product?.name}`
					: product?.name}
			</p>
		</div>
		<div class="flex flex-col">
			<div class="mx-auto flex w-fit items-center">
				<p class="text-lg">{m.cart_subtotal()}</p>
				<div class="size-10 *:size-10">
					<CartButton />
				</div>
			</div>
			<p class="text-center text-2xl font-bold">
				{#if cart.isLoading}
					<Skeleton class="mx-auto h-6" tag="span" />
				{:else}
					{#if cart.subtotal !== cart.total}
						<span class="text-base font-normal line-through">
							<Price price={cart.subtotal} />
						</span>
					{/if}
					<Price price={cart.total} />
				{/if}

				{#if !cart.isLoading && product && cart.cartDiff && product.sku in cart.cartDiff}
					{#if cart.cartDiff?.[product.sku].state === 'updated' && 'price' in cart.cartDiff[product.sku].details}
						<br />
						<span class="text-sm font-normal text-red-500">{m['cart_errors.price']()}</span>
					{/if}
					{#if cart.cartDiff?.[product.sku].state === 'updated' && 'quantity' in cart.cartDiff[product.sku].details}
						<br />
						<span class="text-sm font-normal text-red-500">{m['cart_errors.quantity']()}</span>
					{/if}
					{#if cart.cartDiff?.[product.sku].state === 'deleted' && 'quantity' in cart.cartDiff[product.sku].details}
						<br />
						<span class="text-sm font-normal text-red-500"
							>{m['cart_errors.deleted']({ nbDeleted: 1, products: product.name })}</span
						>
					{/if}
				{/if}
			</p>
			{#if cart.isCountryDifferent}
				<div class="mt-5 text-center text-xs">
					* {m['checkout.computed_for_another_country']({
						countryCode: cart.shippingAddress!.country_code,
					})}
				</div>
			{/if}
		</div>
		<a class="mx-auto my-2 w-fit" href="/cart">
			<BigButton>
				{m.view_my_cart()}
			</BigButton>
		</a>
		<button
			class="mx-auto mb-3 flex cursor-pointer items-center gap-1 font-semibold underline transition duration-200 hover:text-neutral-400"
			onclick={() => (show = false)}
		>
			<span class="icon-[mdi--arrow-left]"></span>
			{m.continue_shopping()}
		</button>
		<Lazy>
			{#await cart.getRecommendations() then recommendedItems}
				{#if recommendedItems?.length}
					<div class="bg-neutral-100 p-4">
						<ProductCarousel products={recommendedItems} capitalizeTitle={false}>
							{m.cart_completion_suggestions()}
						</ProductCarousel>
					</div>
				{/if}
			{/await}
		</Lazy>
	</div>
</div>
